<template>
  <div v-if="needsReconnect">
    <!-- Persistent banner. Not dismissible: the condition it reports lasts
         until the seller reconnects, and the page around it looks healthy
         (comments keep arriving over the webhook), so a banner she can
         close is a banner that stops telling her anything. -->
    <div class="rounded-2xl bg-rose-50 ring-1 ring-rose-200 p-4 sm:p-5">
      <div class="flex items-start gap-3">
        <span
          class="shrink-0 inline-flex size-9 items-center justify-center rounded-xl
                 bg-white text-rose-600 ring-1 ring-rose-200"
        >
          <ExclamationTriangleIcon class="size-5" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-bold text-gray-900">
            {{ t('plugins.connectionAlert.title') }}
          </h3>
          <p class="mt-1 text-sm text-gray-700">
            {{ t('plugins.connectionAlert.body') }}
          </p>
          <div class="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold
                     text-white hover:bg-teal-700 transition"
              @click="reconnect"
            >
              {{ t('plugins.connectionAlert.cta') }}
            </button>
            <span class="text-xs text-gray-500">
              {{ t('plugins.connectionAlert.reassurance') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- One-per-session modal. Only the dashboard opts in (`showModal`), and
         only once per browser session — repeated on every navigation it
         would train the seller to dismiss without reading, and the banner
         above already carries the message permanently. -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/50 p-4"
      role="dialog"
      aria-modal="true"
      :aria-label="t('plugins.connectionAlert.title')"
    >
      <div class="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 p-6">
        <span
          class="inline-flex size-11 items-center justify-center rounded-xl
                 bg-rose-50 text-rose-600 ring-1 ring-rose-200"
        >
          <ExclamationTriangleIcon class="size-6" />
        </span>
        <h3 class="mt-4 text-lg font-bold text-gray-900">
          {{ t('plugins.connectionAlert.title') }}
        </h3>
        <p class="mt-2 text-sm text-gray-600">
          {{ t('plugins.connectionAlert.body') }}
        </p>
        <ul class="mt-4 space-y-2 rounded-xl bg-gray-50 ring-1 ring-gray-100 p-3">
          <li class="flex items-center gap-2 text-xs text-gray-700">
            <CheckCircleIcon class="size-4 shrink-0 text-emerald-600" />
            {{ t('plugins.connectionAlert.stillWorks') }}
          </li>
          <li class="flex items-center gap-2 text-xs text-gray-700">
            <XCircleIcon class="size-4 shrink-0 text-rose-600" />
            {{ t('plugins.connectionAlert.stopped') }}
          </li>
        </ul>
        <div class="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            class="rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-700
                   ring-1 ring-gray-300 hover:bg-gray-50 transition"
            @click="dismissModal"
          >
            {{ t('plugins.connectionAlert.later') }}
          </button>
          <button
            type="button"
            class="rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold
                   text-white hover:bg-teal-700 transition"
            @click="reconnect"
          >
            {{ t('plugins.connectionAlert.cta') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon,
} from '@heroicons/vue/24/outline';
import usePluginStore from '@/store/plugin/plugin';
import Plugin from '@/models/plugin/plugin';
import { buildInstagramOauthUrl, newOauthState } from '@/utils/instagramOauth';

const props = defineProps<{
  plugin?: Plugin | null;
  /** Opt in to the once-per-session modal. Only the dashboard sets it. */
  showModal?: boolean;
}>();

const { t } = useI18n();
const pluginStore = usePluginStore();

const needsReconnect = computed(() => {
  const p = props.plugin;
  // `needsReconnect` lives on the model so every surface agrees on what
  // "broken" means. Guarded because layouts seed `plugin` with `{} as Plugin`
  // before the fetch resolves, so the method may genuinely be absent.
  return !!p && typeof p.needsReconnect === 'function' && p.needsReconnect();
});

// Keyed per plugin: a seller with two accounts should be warned about each.
const seenKey = computed(() => `livelink_reconnect_seen_${props.plugin?.id || ''}`);
const dismissed = ref(false);

const modalOpen = computed(() => {
  if (!props.showModal || !needsReconnect.value || dismissed.value) return false;
  try {
    return sessionStorage.getItem(seenKey.value) !== '1';
  } catch {
    // Private mode / storage disabled — show it. Better one extra prompt
    // than silently swallowing the only warning she gets.
    return true;
  }
});

// Reset when the seller switches account so the new plugin gets its own
// evaluation instead of inheriting the previous one's dismissal.
watch(() => props.plugin?.id, () => { dismissed.value = false; });

function dismissModal() {
  dismissed.value = true;
  try {
    sessionStorage.setItem(seenKey.value, '1');
  } catch { /* storage unavailable — it just reappears next navigation */ }
}

function reconnect() {
  const state = newOauthState();
  pluginStore.csrf = state;
  window.location.href = buildInstagramOauthUrl(state);
}
</script>
